/**
 * The age_range values checkout submits on insurance order items. `senior` is
 * deliberately absent: seniors are quoted via number_of_seniors and
 * svc-insurance converts the matching number of submitted adults to seniors at
 * subscription-create time.
 */
export type InsuranceSubmittedAgeRange = 'adult' | 'child' | 'infant'

export interface AlignedInsuranceTravellerDetail {
  firstname: string | undefined
  surname: string | undefined
  age_range: InsuranceSubmittedAgeRange
}

const REMAINING_SLOT_FILL_ORDER: Array<InsuranceSubmittedAgeRange> = ['adult', 'child', 'infant']

/**
 * The age group a traveller form naturally belongs to, from its form-id prefix,
 * sanitised to the values svc-insurance accepts (ids such as `recipient` or
 * `customer-adult-1` would otherwise leak invalid age ranges).
 */
function getFormAgeRange(formId: string): InsuranceSubmittedAgeRange {
  const prefix = formId.split('-')[0]
  if (prefix === 'child') return 'child'
  if (prefix === 'infant') return 'infant'
  return 'adult'
}

function allocateFormsToQuotedSlots(
  naturalAgeRanges: Array<InsuranceSubmittedAgeRange>,
  quotedTravellers: NonNullable<App.InsuranceQuote['requestedTravellers']>,
): Array<InsuranceSubmittedAgeRange> {
  const remainingSlots: Record<InsuranceSubmittedAgeRange, number> = {
    adult: quotedTravellers.adults + quotedTravellers.seniors,
    child: quotedTravellers.children,
    infant: quotedTravellers.infants,
  }

  // Pass 1 — natural assignments: a form only loses its own age group when that
  // group's quoted slots are exhausted, never to an earlier unmatched form.
  const naturalAssignments: Array<InsuranceSubmittedAgeRange | undefined> = []
  for (const naturalAgeRange of naturalAgeRanges) {
    const hasSlot = remainingSlots[naturalAgeRange] > 0
    if (hasSlot) {
      remainingSlots[naturalAgeRange] -= 1
    }
    naturalAssignments.push(hasSlot ? naturalAgeRange : undefined)
  }

  // Pass 2 — leftover forms fill the remaining slots; surplus forms past the
  // quoted total keep their natural group.
  const assignedAgeRanges: Array<InsuranceSubmittedAgeRange> = []
  for (const [index, naturalAssignment] of naturalAssignments.entries()) {
    if (naturalAssignment) {
      assignedAgeRanges.push(naturalAssignment)
      continue
    }
    const remainingSlot = REMAINING_SLOT_FILL_ORDER.find(ageRange => remainingSlots[ageRange] > 0)
    if (remainingSlot) {
      remainingSlots[remainingSlot] -= 1
    }
    assignedAgeRanges.push(remainingSlot ?? naturalAgeRanges[index])
  }

  return assignedAgeRanges
}

/**
 * Builds the travellers_details for an insurance order item so that its
 * age-group composition equals the composition the selected quote was requested
 * (and priced) for.
 *
 * The quoted composition is the source of truth for age_range: the insurer only
 * receives names and a count on the booking payload, while svc-insurance uses
 * age_range for senior conversion, insured-list padding and amendment
 * re-pricing — all keyed to the quote. Deriving age_range independently (e.g.
 * from typed dates of birth, whose age classification need not match the
 * occupancy slot that was priced) lets the submitted composition silently drift
 * from the quoted one.
 *
 * Allocation: each form first takes a slot of its own form-id group while that
 * group has quoted slots left; leftover forms then fill the remaining slots in
 * a fixed adult, child, infant order. Assignment order cannot change the
 * resulting composition — only which traveller carries which label.
 * - More forms than quoted slots: surplus forms keep their form-id group (the
 *   count mismatch is logged separately by the order-creation validator).
 * - Fewer forms than quoted slots: the submitted subset stays within the quoted
 *   per-group counts, and svc-insurance pads the insured list up to the quote
 *   with the same per-group deficit logic.
 *
 * Falls back to the sanitised form-id group when the quote carries no requested
 * composition (quotes stored before that field existed).
 */
export function alignTravellersToQuotedComposition(
  travellerForms: Array<App.Checkout.TravellerForm>,
  quotedTravellers: App.InsuranceQuote['requestedTravellers'],
): Array<AlignedInsuranceTravellerDetail> {
  const naturalAgeRanges = travellerForms.map(form => getFormAgeRange(form.id))

  const totalQuotedSlots = quotedTravellers
    ? quotedTravellers.adults + quotedTravellers.children + quotedTravellers.infants + quotedTravellers.seniors
    : 0

  const assignedAgeRanges = quotedTravellers && totalQuotedSlots > 0
    ? allocateFormsToQuotedSlots(naturalAgeRanges, quotedTravellers)
    : naturalAgeRanges

  return travellerForms.map((form, index) => ({
    firstname: form.data.firstName,
    surname: form.data.lastName,
    age_range: assignedAgeRanges[index],
  }))
}
